import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { money, fmtD } from "@/lib/semanas";
import { Var } from "@/components/control/cneShared";

function TrendTip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow px-3 py-2 text-xs">
      <p className="font-semibold text-slate-800">Semana {p.week} {p.year}</p>
      <p className="text-slate-500">{fmtD(p.vigencia_inicio)} – {fmtD(p.vigencia_fin)}</p>
      <p className="mt-1">$/kg: <b>{money(p.ponderado_kg)}</b></p>
      <p>$/litro: <b>{money(p.ponderado_lt)}</b></p>
      {p.ponderado_kg == null && <p className="text-amber-600">Sin información suficiente</p>}
    </div>
  );
}

export function TendenciaDialog({ planta, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: ["cne", "tendencia", planta],
    queryFn: async () => (await api.get("/control-semanal/cne/plantas/tendencia", { params: { planta } })).data,
  });
  const pts = (data?.points || []).map((p) => ({ ...p, label: `S${p.week} ${p.year}` }));
  const valid = pts.filter((p) => p.ponderado_kg != null);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl bg-white text-slate-900" data-testid="cne-tendencia-dialog">
        <DialogHeader><DialogTitle>Tendencia del precio ponderado · {planta}</DialogTitle></DialogHeader>
        {isLoading ? <Loader2 className="w-6 h-6 text-red-600 animate-spin" /> : !data?.relaciones?.length ? (
          <p className="text-sm text-slate-500" data-testid="cne-tendencia-empty">La planta no tiene relaciones territoriales asignadas.</p>
        ) : (
          <div className="space-y-4">
            <p className="text-xs text-slate-500">Calculado con las relaciones y ponderaciones actuales de la planta sobre cada publicación oficial cargada ({pts.length} semanas{valid.length < pts.length ? `, ${pts.length - valid.length} sin información suficiente` : ""}).</p>
            <div className="h-72" data-testid="cne-tendencia-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={pts} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} />
                  <YAxis yAxisId="kg" tick={{ fontSize: 11, fill: "#64748b" }} domain={["auto", "auto"]} tickFormatter={(v) => `$${v.toFixed(2)}`} />
                  <YAxis yAxisId="lt" orientation="right" tick={{ fontSize: 11, fill: "#64748b" }} domain={["auto", "auto"]} tickFormatter={(v) => `$${v.toFixed(2)}`} />
                  <Tooltip content={<TrendTip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line yAxisId="kg" type="monotone" dataKey="ponderado_kg" name="Ponderado $/kg" stroke="#dc2626" strokeWidth={2.5} dot={{ r: 4 }} connectNulls={false} />
                  <Line yAxisId="lt" type="monotone" dataKey="ponderado_lt" name="Ponderado $/litro" stroke="#0f172a" strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="max-h-56 overflow-auto rounded-xl border border-slate-100">
              <table className="w-full text-sm" data-testid="cne-tendencia-table">
                <thead className="sticky top-0 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                  <tr><th className="text-left px-3 py-2">Semana</th><th className="text-left px-3 py-2">Vigencia</th><th className="text-right px-3 py-2">Ponderado $/kg</th><th className="text-right px-3 py-2">Ponderado $/litro</th><th className="text-right px-3 py-2">Var. $/kg</th></tr>
                </thead>
                <tbody>
                  {[...pts].reverse().map((p) => (
                    <tr key={p.pub_id} className="border-t border-slate-100">
                      <td className="px-3 py-1.5 font-semibold">S{p.week} {p.year}</td>
                      <td className="px-3 py-1.5 text-slate-500">{fmtD(p.vigencia_inicio)} – {fmtD(p.vigencia_fin)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{p.ponderado_kg == null ? <span className="text-amber-600 text-xs">Sin información suficiente</span> : money(p.ponderado_kg)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{p.ponderado_lt == null ? "—" : money(p.ponderado_lt)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums"><Var v={p.var_kg} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
